import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const OperationalHoursSection = ({ hours, onUpdate, isEditing, onToggleEdit }) => {
  const [formData, setFormData] = useState(hours);

  const timezones = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' }
  ];

  const timeSlots = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const time24 = `${hour?.toString()?.padStart(2, '0')}:${minute?.toString()?.padStart(2, '0')}`;
      const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const ampm = hour < 12 ? 'AM' : 'PM';
      const time12 = `${hour12}:${minute?.toString()?.padStart(2, '0')} ${ampm}`;
      timeSlots?.push({ value: time24, label: time12 });
    }
  }

  const daysOfWeek = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' }
  ];

  const holidays = [
    { key: 'newYear', label: "New Year\'s Day" },
    { key: 'martinLutherKing', label: 'Martin Luther King Jr. Day' },
    { key: 'presidents', label: "Presidents\' Day" },
    { key: 'memorial', label: 'Memorial Day' },
    { key: 'independence', label: 'Independence Day' },
    { key: 'labor', label: 'Labor Day' },
    { key: 'columbus', label: 'Columbus Day' },
    { key: 'veterans', label: 'Veterans Day' },
    { key: 'thanksgiving', label: 'Thanksgiving' },
    { key: 'christmas', label: 'Christmas Day' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDayScheduleChange = (day, field, value) => {
    setFormData(prev => ({
      ...prev,
      weeklySchedule: {
        ...prev?.weeklySchedule,
        [day]: {
          ...prev?.weeklySchedule?.[day],
          [field]: value
        }
      }
    }));
  };

  const handleHolidayChange = (holiday, checked) => {
    setFormData(prev => ({
      ...prev,
      holidaySchedule: {
        ...prev?.holidaySchedule,
        [holiday]: checked
      }
    }));
  };

  const copyToAllDays = (sourceDay) => {
    const sourceSchedule = formData?.weeklySchedule?.[sourceDay];
    const updatedSchedule = {};
    
    daysOfWeek?.forEach(day => {
      updatedSchedule[day.key] = { ...sourceSchedule };
    });

    setFormData(prev => ({
      ...prev,
      weeklySchedule: updatedSchedule
    }));
  };

  const handleSave = () => {
    onUpdate(formData);
    onToggleEdit();
  };

  const handleCancel = () => {
    setFormData(hours);
    onToggleEdit();
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
            <Icon name="Clock" size={20} className="text-warning" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Operational Hours</h3>
            <p className="text-sm text-muted-foreground">Configure when you're available to receive calls</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {isEditing ? (
            <>
              <Button variant="outline" size="sm" onClick={handleCancel}>
                Cancel
              </Button>
              <Button variant="default" size="sm" onClick={handleSave}>
                Save Changes
              </Button>
            </>
          ) : (
            <Button variant="outline" size="sm" onClick={onToggleEdit} iconName="Edit2">
              Edit Hours
            </Button>
          )}
        </div>
      </div>
      <div className="space-y-6">
        {/* Timezone Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Primary Timezone"
            options={timezones}
            value={formData?.primaryTimezone}
            onChange={(value) => handleInputChange('primaryTimezone', value)}
            disabled={!isEditing}
            required
          />
          <div className="flex items-end">
            <Checkbox
              label="Accept calls in all US timezones"
              checked={formData?.acceptAllTimezones || false}
              onChange={(e) => handleInputChange('acceptAllTimezones', e?.target?.checked)}
              disabled={!isEditing}
            />
          </div>
        </div>

        {/* Weekly Schedule */}
        <div>
          <h4 className="text-sm font-medium text-foreground mb-4">Weekly Schedule</h4>
          <div className="space-y-3">
            {daysOfWeek?.map((day) => (
              <div key={day?.key} className="grid grid-cols-1 md:grid-cols-5 gap-4 items-center p-3 bg-muted/30 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    checked={formData?.weeklySchedule?.[day?.key]?.enabled || false}
                    onChange={(e) => handleDayScheduleChange(day?.key, 'enabled', e?.target?.checked)}
                    disabled={!isEditing}
                  />
                  <span className="text-sm font-medium text-foreground min-w-[80px]">
                    {day?.label}
                  </span>
                </div>
                
                <Select
                  placeholder="Start time"
                  options={timeSlots}
                  value={formData?.weeklySchedule?.[day?.key]?.startTime || ''}
                  onChange={(value) => handleDayScheduleChange(day?.key, 'startTime', value)}
                  disabled={!isEditing || !formData?.weeklySchedule?.[day?.key]?.enabled}
                />
                
                <Select
                  placeholder="End time"
                  options={timeSlots}
                  value={formData?.weeklySchedule?.[day?.key]?.endTime || ''}
                  onChange={(value) => handleDayScheduleChange(day?.key, 'endTime', value)}
                  disabled={!isEditing || !formData?.weeklySchedule?.[day?.key]?.enabled}
                />
                
                <Select
                  placeholder="Break start"
                  options={timeSlots}
                  value={formData?.weeklySchedule?.[day?.key]?.breakStart || ''}
                  onChange={(value) => handleDayScheduleChange(day?.key, 'breakStart', value)}
                  disabled={!isEditing || !formData?.weeklySchedule?.[day?.key]?.enabled}
                />
                
                <div className="flex items-center space-x-2">
                  <Select
                    placeholder="Break end"
                    options={timeSlots}
                    value={formData?.weeklySchedule?.[day?.key]?.breakEnd || ''}
                    onChange={(value) => handleDayScheduleChange(day?.key, 'breakEnd', value)}
                    disabled={!isEditing || !formData?.weeklySchedule?.[day?.key]?.enabled}
                  />
                  {isEditing && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToAllDays(day?.key)}
                      iconName="Copy"
                      title="Copy to all days"
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Holiday Schedule */}
        <div>
          <h4 className="text-sm font-medium text-foreground mb-4">Holiday Availability</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {holidays?.map((holiday) => (
              <Checkbox
                key={holiday?.key}
                label={holiday?.label}
                checked={formData?.holidaySchedule?.[holiday?.key] || false}
                onChange={(e) => handleHolidayChange(holiday?.key, e?.target?.checked)}
                disabled={!isEditing}
              />
            ))}
          </div>
        </div>

        {/* Quick Settings */}
        <div className="bg-muted/30 rounded-lg p-4">
          <h4 className="text-sm font-medium text-foreground mb-3">Quick Settings</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Checkbox
              label="24/7 Availability"
              checked={formData?.alwaysAvailable || false}
              onChange={(e) => handleInputChange('alwaysAvailable', e?.target?.checked)}
              disabled={!isEditing}
            />
            <Checkbox
              label="Weekend Operations"
              checked={formData?.weekendOperations || false}
              onChange={(e) => handleInputChange('weekendOperations', e?.target?.checked)}
              disabled={!isEditing}
            />
            <Checkbox
              label="After Hours Emergency"
              checked={formData?.afterHoursEmergency || false}
              onChange={(e) => handleInputChange('afterHoursEmergency', e?.target?.checked)}
              disabled={!isEditing}
            />
            <Checkbox
              label="Auto-Pause During Breaks"
              checked={formData?.autoPauseDuringBreaks || false}
              onChange={(e) => handleInputChange('autoPauseDuringBreaks', e?.target?.checked)}
              disabled={!isEditing}
            />
          </div>
        </div>

        {/* Current Status */}
        <div className="flex items-center justify-between p-4 bg-success/10 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 bg-success rounded-full pulse-live"></div>
            <div>
              <p className="text-sm font-medium text-success">Currently Available</p>
              <p className="text-xs text-muted-foreground">
                Next break: 12:00 PM - 1:00 PM EST
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" iconName="Pause">
            Pause Temporarily
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OperationalHoursSection;